import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const RecaudacionPeriodo = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const [recaudAgrupacion, setRecaudAgrupacion] = useState("mes");
  const [recaudFechaDesde, setRecaudFechaDesde] = useState("");
  const [recaudFechaHasta, setRecaudFechaHasta] = useState("");
  const [recaudOficina, setRecaudOficina] = useState("todas");
  const [recaudDesglose, setRecaudDesglose] = useState("ninguno");
  const [recaudFilters, setRecaudFilters] = useState({ periodo: "", oficina: "", comprobantes: "", base: "", multas: "", descuento: "", total: "" });

  useEffect(() => { fetchRecaudacionData(); }, []);

  const fetchRecaudacionData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      let url = `${API}/reportes/recaudacion-por-periodo`;
      const params = new URLSearchParams();
      params.append("agrupacion", recaudAgrupacion);
      if (aplicarFiltros || recaudFechaDesde) { if (recaudFechaDesde) params.append("fecha_desde", recaudFechaDesde); }
      if (aplicarFiltros || recaudFechaHasta) { if (recaudFechaHasta) params.append("fecha_hasta", recaudFechaHasta); }
      if (recaudOficina !== "todas") params.append("oficina", recaudOficina);
      if (recaudDesglose !== "ninguno") params.append("desglose", recaudDesglose);
      url += `?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setReporteData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados correctamente");
    } catch (error) {
      toast.error("Error al cargar datos de recaudación");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosRecaud = () => {
    setRecaudFechaDesde("");
    setRecaudFechaHasta("");
    setRecaudOficina("todas");
    setRecaudDesglose("ninguno");
    setRecaudFilters({ periodo: "", oficina: "", comprobantes: "", base: "", multas: "", descuento: "", total: "" });
    fetchRecaudacionData(false);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosRecaudActivos = recaudFechaDesde || recaudFechaHasta || recaudOficina !== "todas" || recaudDesglose !== "ninguno";
  const updateRecaudFilter = (key, value) => setRecaudFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosRecaudColActivos = Object.values(recaudFilters).some(v => v !== "");

  const filteredRecaudFilas = reporteData?.filas?.filter(f => {
    const rf = recaudFilters;
    if (rf.periodo && !(f.periodo_label || "").toLowerCase().includes(rf.periodo.toLowerCase())) return false;
    if (rf.oficina && !(f.oficina || "").toLowerCase().includes(rf.oficina.toLowerCase())) return false;
    if (rf.comprobantes && !String(f.nro_comprobantes || 0).includes(rf.comprobantes)) return false;
    if (rf.base && !String(f.base_imponible || 0).includes(rf.base)) return false;
    if (rf.multas && !String(f.multas || 0).includes(rf.multas)) return false;
    if (rf.descuento && !String(f.descuento || 0).includes(rf.descuento)) return false;
    if (rf.total && !String(f.total_recaudado || 0).includes(rf.total)) return false;
    return true;
  }) || [];

  const recaudTotalesFiltrados = filteredRecaudFilas.reduce((acc, f) => ({
    nro_comprobantes: acc.nro_comprobantes + (f.nro_comprobantes || 0),
    base_imponible: acc.base_imponible + (f.base_imponible || 0),
    multas: acc.multas + (f.multas || 0),
    descuento: acc.descuento + (f.descuento || 0),
    total_recaudado: acc.total_recaudado + (f.total_recaudado || 0),
  }), { nro_comprobantes: 0, base_imponible: 0, multas: 0, descuento: 0, total_recaudado: 0 });

  const exportRecaudToExcel = () => {
    if (!reporteData) return;
    const agLabel = recaudAgrupacion === "mes" ? "Mes" : recaudAgrupacion === "trimestre" ? "Trimestre" : "Año";
    const wsData = [
      ["RECAUDACIÓN POR PERÍODO"],
      [`Agrupación: ${agLabel}`],
      [],
      ["PERÍODO", recaudDesglose === "oficina" ? "OFICINA" : "", "NRO. COMPROBANTES", "BASE IMPONIBLE", "(+) MULTAS", "(-) DCTO.", "TOTAL RECAUDADO", "%"].filter(Boolean)
    ];
    const grandTotal = recaudTotalesFiltrados.total_recaudado;
    filteredRecaudFilas.forEach(f => {
      const pct = grandTotal > 0 ? ((f.total_recaudado / grandTotal) * 100).toFixed(1) : "0.0";
      const row = [f.periodo_label, f.nro_comprobantes, f.base_imponible, f.multas, f.descuento, f.total_recaudado, parseFloat(pct)];
      if (recaudDesglose === "oficina") row.splice(1, 0, f.oficina);
      wsData.push(row);
    });
    wsData.push([]);
    const totalRow = ["TOTALES", recaudTotalesFiltrados.nro_comprobantes, recaudTotalesFiltrados.base_imponible, recaudTotalesFiltrados.multas, recaudTotalesFiltrados.descuento, recaudTotalesFiltrados.total_recaudado, 100];
    if (recaudDesglose === "oficina") totalRow.splice(1, 0, "");
    wsData.push(totalRow);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "RecaudacionPeriodo");
    XLSX.writeFile(wb, `Recaudacion_Periodo_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportRecaudToPDF = () => {
    if (!reporteData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(16);
    doc.setTextColor(22, 163, 74);
    doc.text("RECAUDACIÓN POR PERÍODO", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    const agLabel = recaudAgrupacion === "mes" ? "Mensual" : recaudAgrupacion === "trimestre" ? "Trimestral" : "Anual";
    doc.text(`Agrupación: ${agLabel}`, 14, 25);
    const headCols = ["Período", "Nro. Comps.", "Base Imponible", "(+) Multas", "(-) Dcto.", "Total Recaudado", "%"];
    if (recaudDesglose === "oficina") headCols.splice(1, 0, "Oficina");
    const grandTotal = recaudTotalesFiltrados.total_recaudado;
    const body = filteredRecaudFilas.map(f => {
      const pct = grandTotal > 0 ? ((f.total_recaudado / grandTotal) * 100).toFixed(1) + "%" : "0.0%";
      const row = [f.periodo_label, f.nro_comprobantes, fmtBs(f.base_imponible), fmtBs(f.multas), fmtBs(f.descuento), fmtBs(f.total_recaudado), pct];
      if (recaudDesglose === "oficina") row.splice(1, 0, f.oficina);
      return row;
    });
    const totalRow = ["TOTALES", recaudTotalesFiltrados.nro_comprobantes, fmtBs(recaudTotalesFiltrados.base_imponible), fmtBs(recaudTotalesFiltrados.multas), fmtBs(recaudTotalesFiltrados.descuento), fmtBs(recaudTotalesFiltrados.total_recaudado), "100%"];
    if (recaudDesglose === "oficina") totalRow.splice(1, 0, "");
    body.push(totalRow);
    doc.autoTable({
      startY: 32,
      head: [headCols],
      body,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [22, 163, 74], textColor: 255 },
      alternateRowStyles: { fillColor: [240, 253, 244] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [220, 252, 231]; } }
    });
    doc.save(`Recaudacion_Periodo_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportRecaudToCSV = () => {
    if (!reporteData) return;
    const cols = ["PERÍODO", "NRO. COMPROBANTES", "BASE IMPONIBLE", "(+) MULTAS", "(-) DCTO.", "TOTAL RECAUDADO", "%"];
    if (recaudDesglose === "oficina") cols.splice(1, 0, "OFICINA");
    const grandTotal = recaudTotalesFiltrados.total_recaudado;
    const rows = filteredRecaudFilas.map(f => {
      const pct = grandTotal > 0 ? ((f.total_recaudado / grandTotal) * 100).toFixed(1) : "0.0";
      const row = [`"${f.periodo_label}"`, f.nro_comprobantes, f.base_imponible, f.multas, f.descuento, f.total_recaudado, pct];
      if (recaudDesglose === "oficina") row.splice(1, 0, `"${f.oficina}"`);
      return row.join(",");
    });
    const blob = new Blob([cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Recaudacion_Periodo_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const showOficinaCol = recaudDesglose === "oficina";
  const colSpanTotal = showOficinaCol ? 8 : 7;
  return (
    <div className="space-y-6" data-testid="reporte-recaudacion-periodo">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="recaud-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recaudación por Período
            </h1>
            <p className="text-slate-500">Análisis de recaudación por {recaudAgrupacion === "mes" ? "mes" : recaudAgrupacion === "trimestre" ? "trimestre" : "año"}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchRecaudacionData(false)} disabled={loading} data-testid="recaud-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!reporteData} data-testid="recaud-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportRecaudToExcel} data-testid="recaud-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecaudToPDF} data-testid="recaud-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecaudToCSV} data-testid="recaud-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-green-200 bg-green-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[140px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Agrupación</Label>
              <Select value={recaudAgrupacion} onValueChange={setRecaudAgrupacion}>
                <SelectTrigger className="bg-white" data-testid="recaud-agrupacion">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mes">Mensual</SelectItem>
                  <SelectItem value="trimestre">Trimestral</SelectItem>
                  <SelectItem value="año">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Desde</Label>
              <Input type="date" value={recaudFechaDesde} onChange={(e) => setRecaudFechaDesde(e.target.value)} className="bg-white" data-testid="recaud-fecha-desde" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Hasta</Label>
              <Input type="date" value={recaudFechaHasta} onChange={(e) => setRecaudFechaHasta(e.target.value)} className="bg-white" data-testid="recaud-fecha-hasta" />
            </div>
            <div className="w-[140px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Oficina</Label>
              <Select value={recaudOficina} onValueChange={setRecaudOficina}>
                <SelectTrigger className="bg-white" data-testid="recaud-oficina">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="Hacienda">Hacienda</SelectItem>
                  <SelectItem value="Catastro">Catastro</SelectItem>
                  <SelectItem value="Publicidad">Publicidad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[140px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Desglose</Label>
              <Select value={recaudDesglose} onValueChange={setRecaudDesglose}>
                <SelectTrigger className="bg-white" data-testid="recaud-desglose">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ninguno">Ninguno</SelectItem>
                  <SelectItem value="oficina">Por Oficina</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchRecaudacionData(true)} disabled={loading} className="bg-green-600 hover:bg-green-700" data-testid="recaud-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosRecaudActivos && (
                <Button variant="outline" onClick={limpiarFiltrosRecaud} disabled={loading} data-testid="recaud-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-green-400" />
        </div>
      ) : reporteData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Comprobantes</p>
                <p className="text-2xl font-bold text-slate-900">{reporteData.resumen?.total_comprobantes || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Períodos</p>
                <p className="text-2xl font-bold text-green-700">{reporteData.resumen?.total_periodos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">(+) Multas</p>
                <p className="text-lg font-bold text-red-600">{fmtBs(reporteData.resumen?.total_multas)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">(-) Dcto.</p>
                <p className="text-lg font-bold text-amber-600">{fmtBs(reporteData.resumen?.total_descuento)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Recaudado</p>
                <p className="text-lg font-bold text-green-700">{fmtBs(reporteData.resumen?.total_recaudado)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de barras */}
          {filteredRecaudFilas.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tendencia de Recaudación</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredRecaudFilas}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periodo_label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => [fmtBs(value), ""]} labelStyle={{ fontWeight: "bold" }} />
                    <Legend />
                    <Bar dataKey="total_recaudado" name="Total Recaudado" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Tabla */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle por Período</CardTitle>
                  <CardDescription>{filteredRecaudFilas.length} de {reporteData.filas?.length || 0} períodos | Promedio: {fmtBs(reporteData.resumen?.promedio_por_periodo)}</CardDescription>
                </div>
                {hayFiltrosRecaudColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setRecaudFilters({ periodo: "", oficina: "", comprobantes: "", base: "", multas: "", descuento: "", total: "" })} data-testid="recaud-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="recaud-tabla">
                  <thead>
                    <tr className="border-b-2 border-green-200 bg-green-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-green-800 uppercase">Período</th>
                      {showOficinaCol && <th className="text-left py-3 px-3 text-xs font-semibold text-green-800 uppercase">Oficina</th>}
                      <th className="text-right py-3 px-3 text-xs font-semibold text-green-800 uppercase">Nro. Comprobantes</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-green-800 uppercase">Base Imponible</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-green-800 uppercase">(+) Multas</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-green-800 uppercase">(-) Dcto.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-green-800 uppercase">Total Recaudado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-green-800 uppercase">%</th>
                    </tr>
                    <tr className="bg-green-50/60 border-b border-green-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={recaudFilters.periodo} onChange={(e) => updateRecaudFilter("periodo", e.target.value)} data-testid="recaud-filter-periodo" /></th>
                      {showOficinaCol && <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={recaudFilters.oficina} onChange={(e) => updateRecaudFilter("oficina", e.target.value)} data-testid="recaud-filter-oficina" /></th>}
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recaudFilters.comprobantes} onChange={(e) => updateRecaudFilter("comprobantes", e.target.value)} data-testid="recaud-filter-comprobantes" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recaudFilters.base} onChange={(e) => updateRecaudFilter("base", e.target.value)} data-testid="recaud-filter-base" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recaudFilters.multas} onChange={(e) => updateRecaudFilter("multas", e.target.value)} data-testid="recaud-filter-multas" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recaudFilters.descuento} onChange={(e) => updateRecaudFilter("descuento", e.target.value)} data-testid="recaud-filter-descuento" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recaudFilters.total} onChange={(e) => updateRecaudFilter("total", e.target.value)} data-testid="recaud-filter-total" /></th>
                      <th className="px-2 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecaudFilas.length === 0 ? (
                      <tr><td colSpan={colSpanTotal} className="text-center py-12 text-slate-400">No se encontraron registros</td></tr>
                    ) : (
                      filteredRecaudFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-green-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm font-medium text-slate-800">{f.periodo_label}</td>
                          {showOficinaCol && <td className="py-2.5 px-3 text-sm"><span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">{f.oficina}</span></td>}
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{f.nro_comprobantes}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.base_imponible)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-red-600">{fmtBs(f.multas)}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-amber-600">{fmtBs(f.descuento)}</td>
                          <td className="py-2.5 px-3 text-sm text-right font-semibold text-green-700">{fmtBs(f.total_recaudado)}</td>
                          <td className="py-2.5 px-3 text-sm text-right" data-testid={`recaud-porcentaje-${idx}`}>
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-2 bg-green-100 rounded-full overflow-hidden">
                                <div className="h-full bg-green-500 rounded-full" style={{ width: `${recaudTotalesFiltrados.total_recaudado > 0 ? Math.min((f.total_recaudado / recaudTotalesFiltrados.total_recaudado) * 100, 100) : 0}%` }}></div>
                              </div>
                              <span className="text-green-700 font-medium">{recaudTotalesFiltrados.total_recaudado > 0 ? ((f.total_recaudado / recaudTotalesFiltrados.total_recaudado) * 100).toFixed(1) : "0.0"}</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredRecaudFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-green-100 border-t-2 border-green-300">
                        <td colSpan={showOficinaCol ? 2 : 1} className="py-3 px-3 text-sm font-bold text-green-900 uppercase">Totales</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-green-900">{recaudTotalesFiltrados.nro_comprobantes}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-green-900">{fmtBs(recaudTotalesFiltrados.base_imponible)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-red-700">{fmtBs(recaudTotalesFiltrados.multas)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-amber-700">{fmtBs(recaudTotalesFiltrados.descuento)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-green-700">{fmtBs(recaudTotalesFiltrados.total_recaudado)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-green-700">100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchRecaudacionData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default RecaudacionPeriodo;
